<script lang="ts">
  import { tick } from "svelte";
  import { observe } from "$lib/actions/observe";
  import { POST } from "$lib/api";
  import { isValidDomain } from "$lib/validation/domain";
  import { isValidEmail } from "$lib/validation/email";

  let {
    sectionLabel = "",
    headline = "",
    description = "",
    messages = {
      website: { de: "Bitte geben Sie eine gültige Domain ein (z.B. orf.at)", en: "Please enter a valid domain (e.g. example.com)" },
      name: { de: "Bitte geben Sie Ihren Namen ein", en: "Please enter your name" },
      email: { de: "Bitte geben Sie eine gültige E-Mail-Adresse ein", en: "Please enter a valid email address" }
    },
    successMessage = {
      de: "Vielen Dank! Wir prüfen Ihre Website und melden uns innerhalb von 24 Stunden mit konkretem Feedback.",
      en: "Thank you! We'll check your website and get back to you within 24 hours with concrete feedback."
    }
  }: {
    sectionLabel?: string;
    headline?: string;
    description?: string;
    messages?: Record<string, { de: string; en: string }>;
    successMessage?: { de: string; en: string };
  } = $props();

  let step = $state(1);

  let website = $state("");
  let name = $state("");
  let email = $state("");
  let phone = $state("");
  let role = $state("");
  let company = $state(""); // honeypot — must stay empty

  let submitting = $state(false);
  let submitted = $state(false);
  let submitError = $state(false);
  let errors = $state<Record<string, boolean>>({});

  let successEl = $state<HTMLElement>();
  let errorEl = $state<HTMLElement>();

  function scrollToMessage(el: HTMLElement | undefined) {
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Step 1 → reveal the contact form and capture the website immediately,
  // so we keep the lead even if the visitor doesn't finish step 2.
  function continueToContact() {
    if (!isValidDomain(website)) {
      errors = { website: true };
      return;
    }
    errors = {};
    step = 2;
    POST("leads", { website, company, stage: "start" }).catch((err) => console.error(err));
  }

  function validate() {
    const e: Record<string, boolean> = {};
    if (!isValidDomain(website)) e.website = true;
    if (!name.trim()) e.name = true;
    if (!isValidEmail(email)) e.email = true;
    errors = e;
    return Object.keys(e).length === 0;
  }

  async function handleSubmit() {
    submitError = false;
    if (!validate()) {
      if (errors.website) step = 1;
      return;
    }
    submitting = true;
    try {
      const res = await POST("leads", { website, name, email, phone, role, company, stage: "complete" });
      if (res?.ok) {
        submitted = true;
        await tick();
        scrollToMessage(successEl);
      } else {
        submitError = true;
        await tick();
        scrollToMessage(errorEl);
      }
    } catch (err) {
      console.error(err);
      submitError = true;
      await tick();
      scrollToMessage(errorEl);
    } finally {
      submitting = false;
    }
  }
</script>

<section>
  <div class="container">
    {#if sectionLabel}
      <div class="section-divider fade-up" use:observe>
        <span class="section-label">{sectionLabel}</span>
        <h1>{headline}</h1>
        {#if description}
          <p>{description}</p>
        {/if}
      </div>
    {/if}

    <div class="lead-form fade-up" use:observe>
      {#if submitted}
        <div class="card lead-success" role="status" bind:this={successEl} style="background:var(--success-bg);border-color:var(--success)">
          <p lang="de" style="color:var(--success);font-weight:600">{successMessage.de}</p>
          <p lang="en" style="color:var(--success);font-weight:600">{successMessage.en}</p>
        </div>
      {:else}
        <form
          onsubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          novalidate
        >
          <!-- Step 1: prominent website field -->
          <div class="form-group lead-website-group">
            <label class="lead-website-label" lang="de" for="website">Ihre Website</label>
            <label class="lead-website-label" lang="en" for="website">Your website</label>
            <div class="lead-website-row">
              <input
                id="website"
                class="lead-website-input"
                type="url"
                inputmode="url"
                placeholder="https://"
                bind:value={website}
                onkeydown={(e) => {
                  if (e.key === "Enter" && step === 1) {
                    e.preventDefault();
                    continueToContact();
                  }
                }}
                aria-invalid={!!errors.website}
                aria-describedby={errors.website ? "err-website-de err-website-en" : undefined}
                style={errors.website ? "border-color:var(--accent)" : ""}
              />
              {#if step === 1}
                <button type="button" class="btn btn-primary lead-website-btn" onclick={continueToContact}>
                  <span lang="de">Weiter</span>
                  <span lang="en">Continue</span>
                </button>
              {/if}
            </div>
            {#if errors.website}
              <p id="err-website-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:6px">{messages.website.de}</p>
              <p id="err-website-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:6px">{messages.website.en}</p>
            {/if}
          </div>

          <!-- Step 2: contact details, revealed after the website is entered -->
          {#if step >= 2}
            <div class="lead-contact fade-up" use:observe>
              <p class="lead-contact-intro" lang="de">Fast geschafft — wohin dürfen wir Ihr Feedback schicken?</p>
              <p class="lead-contact-intro" lang="en">Almost there — where should we send your feedback?</p>

              <div class="form-group">
                <label lang="de" for="name">Name *</label>
                <label lang="en" for="name">Name *</label>
                <input id="name" type="text" bind:value={name} aria-invalid={!!errors.name} aria-describedby={errors.name ? "err-lname-de err-lname-en" : undefined} style={errors.name ? "border-color:var(--accent)" : ""} />
                {#if errors.name}
                  <p id="err-lname-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.name.de}</p>
                  <p id="err-lname-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.name.en}</p>
                {/if}
              </div>

              <div class="form-group">
                <label lang="de" for="email">E-Mail *</label>
                <label lang="en" for="email">Email *</label>
                <input id="email" type="email" bind:value={email} aria-invalid={!!errors.email} aria-describedby={errors.email ? "err-lemail-de err-lemail-en" : undefined} style={errors.email ? "border-color:var(--accent)" : ""} />
                {#if errors.email}
                  <p id="err-lemail-de" role="alert" lang="de" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.email.de}</p>
                  <p id="err-lemail-en" role="alert" lang="en" style="color:var(--accent);font-size:0.8rem;margin-top:4px">{messages.email.en}</p>
                {/if}
              </div>

              <div class="form-group">
                <label lang="de" for="phone">Telefon</label>
                <label lang="en" for="phone">Phone</label>
                <input id="phone" type="tel" placeholder="+43" bind:value={phone} />
              </div>

              <div class="form-group">
                <label lang="de" for="role">Ihre Rolle im Unternehmen</label>
                <label lang="en" for="role">Your role in the company</label>
                <input id="role" type="text" bind:value={role} />
              </div>

              <div class="form-group">
                <p style="font-size:0.8rem;color:var(--muted)">
                  <span lang="de">Hinweis: Wie wir Ihre Daten verarbeiten, erfahren Sie in unserer <a href="/datenschutz">Datenschutzerklärung</a>.</span>
                  <span lang="en">Note: Learn how we handle your data in our <a href="/datenschutz">privacy policy</a>.</span>
                </p>
              </div>

              <div class="hp-field" aria-hidden="true">
                <label for="company">Company</label>
                <input id="company" type="text" tabindex="-1" autocomplete="off" bind:value={company} />
              </div>

              {#if submitError}
                <div bind:this={errorEl}>
                  <p role="alert" lang="de" style="color:var(--accent);font-size:0.85rem;margin-bottom:12px">Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.</p>
                  <p role="alert" lang="en" style="color:var(--accent);font-size:0.85rem;margin-bottom:12px">Something went wrong. Please try again.</p>
                </div>
              {/if}

              <button type="submit" class="btn btn-primary" lang="de" disabled={submitting}>Website prüfen lassen</button>
              <button type="submit" class="btn btn-primary" lang="en" disabled={submitting}>Request website check</button>
            </div>
          {/if}
        </form>
      {/if}
    </div>
  </div>
</section>
